import { useContext, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { indexBy, prop } from 'ramda';
import { Box } from '@alf/uikit';
import { LOG_ACTIONS, useLogAmplitude } from '@namespace/analytics';
import {
  getSlidePlatformImage,
  SiteSettingsContext,
  useComponentConfig
} from '@namespace/cms';
import { useOnSlideMove } from '@namespace/common';
import { useDeviceStore } from '@namespace/device';
import { useI18n } from '@namespace/i18n';
import { useUserSegmentsIncludes } from '@namespace/segments';
import { useGetImage } from '@namespace/themes';
import { UserContext } from '@namespace/user';
import {
  CardSkelet,
  EventsSlider,
  SportTeaser,
  WidgetWrapper
} from '../../common';
import {
  LINE_KEY,
  TEASER_TYPES,
  TEASERS_NAVIGATION_TYPES
} from '../../constants';
import { useRemoveLine } from '../../hooks/useRemoveLine';
import {
  getLineField,
  sportsbookActions,
  useLine,
  useSportsbookStore
} from '../../store';
import { getOutcomesForMarket } from '../../utils';
import ImageTeaser from './ImageTeaser';
import PromoTeaser from './PromoTeaser';

import styles from './index.module.css';

const { FETCH_TEASERS2, SET_IS_LINE_CACHED } = sportsbookActions;

const DURATION = 5;
const SLIDE_SPEED = 700;

export const TeasersVersion2 = () => {
  const config = useComponentConfig();
  const { language: lang, f } = useI18n();
  const { pageType } = useDeviceStore();
  const {
    slides: configSlides = [],
    rotationDelay = DURATION,
    height,
    pauseRotationOnFocus = false,
    navigation
  } = config;
  const isBulletsEnabled =
    navigation === TEASERS_NAVIGATION_TYPES.BULLETS ||
    navigation === TEASERS_NAVIGATION_TYPES.BULLETS_AND_ARROWS;
  const isArrowsEnabled =
    navigation === TEASERS_NAVIGATION_TYPES.BULLETS_AND_ARROWS;
  const [user] = useContext(UserContext);
  const { activeWallet } = user;
  const { currency } = activeWallet;
  const [settings] = useContext(SiteSettingsContext);
  const { siteConfig } = settings;
  const { defaultCurrency } = siteConfig;
  // todo refactor: markets & outcomes store access
  const {
    teasers,
    markets,
    outcomes: stateOutcomes
  } = useSportsbookStore(['teasers', 'markets', 'outcomes']);
  const teaserLine = useLine(LINE_KEY.TEASERS2);
  const isTeasersLoading = useSportsbookStore('isTeasersLoading');
  const filterSegments = useUserSegmentsIncludes();
  const { handleClickOnMove, setIsMove } = useOnSlideMove();
  const getImg = useGetImage();

  const { eventsIds = [] } = teaserLine ?? {};
  const slides = useMemo(
    () => configSlides.map((slide, i) => ({ ...slide, order: i })),
    [configSlides]
  );
  const slidesByTeaserName = useMemo(
    () => indexBy(prop('teaserName'), slides),
    [slides]
  );

  const teasersNames = useMemo(
    () => slides.map(({ teaserName }) => teaserName).filter(Boolean),
    [slides]
  );

  const promoTeasers = useMemo(
    () =>
      slides.filter((slide) => {
        const { snippetId, from, to, source } = slide;

        if (
          (source === TEASER_TYPES.HTML_SNIPPET && snippetId) ||
          source === TEASER_TYPES.IMAGE
        ) {
          if (from || to) {
            return f.localTimeIsActual(from, to, f.getDateTime);
          }

          return true;
        }

        return false;
      }),
    [f, slides]
  );

  const logAmplitude = useLogAmplitude();

  const clickOnSlide = ({ slideName, index }) => {
    logAmplitude({
      actionName: LOG_ACTIONS.BANNER_OPEN,
      attributes: {
        entry_point: 'teasers',
        position: index + 1,
        banner_name: slideName
      },
      isMatchPath: true
    });
  };

  useEffect(() => {
    if (teasersNames.length > 0) {
      if (getLineField(LINE_KEY.TEASERS2, 'isCached')) {
        SET_IS_LINE_CACHED(false, LINE_KEY.TEASERS2);
        useSportsbookStore.setState({ isTeasersLoading: false });
        return;
      }

      // todo refactor: isTeasersLoading: make universal line loading state and methods
      FETCH_TEASERS2({
        lineKey: LINE_KEY.TEASERS2,
        teasersNames,
        lang
      });
    } else {
      useSportsbookStore.setState({ isTeasersLoading: false });
    }
  }, [lang, teasersNames]);

  const allSlides = useMemo(
    () =>
      [
        ...teasers
          .map(({ teaserName, ...teaser }) => ({
            ...slidesByTeaserName[teaserName],
            teaserName,
            ...teaser
          }))
          .filter(
            (slide) =>
              eventsIds.includes(slide.eventId) &&
              teasersNames.includes(slide.teaserName)
          ),
        ...promoTeasers
      ]
        .filter((slide) => filterSegments(slide.userSegments))
        .sort((a, b) => a.order - b.order),
    [
      eventsIds,
      filterSegments,
      promoTeasers,
      slidesByTeaserName,
      teasers,
      teasersNames
    ]
  );

  useRemoveLine(LINE_KEY.TEASERS2);

  if (isTeasersLoading) {
    return (
      <WidgetWrapper>
        <Box
          className={clsx(
            styles.general,
            isBulletsEnabled && styles.skeletWithBullets
          )}
        >
          <CardSkelet />
        </Box>
      </WidgetWrapper>
    );
  }

  if (allSlides.length === 0 || !teaserLine) {
    return null;
  }

  return (
    <WidgetWrapper>
      <EventsSlider
        key={allSlides.length}
        sliderOptions={{
          className: styles.general,
          duration: SLIDE_SPEED,
          rotationInterval: rotationDelay,
          height,
          circular: true
        }}
        pauseRotationOnFocus={pauseRotationOnFocus}
        isBulletsEnabled={isBulletsEnabled}
        isArrowsEnabled={isArrowsEnabled}
        setIsMove={setIsMove}
      >
        {allSlides.map((teaser, index) => {
          const { eventId, snippetId, source } = teaser;
          if (snippetId) {
            return (
              <PromoTeaser
                key={teaser.id}
                snippetId={snippetId}
                className={styles.teaser}
                handleClickOnMove={(event) =>
                  handleClickOnMove(event, () => {
                    clickOnSlide({
                      event,
                      slideName: teaser.snippetId,
                      index
                    });
                    event.stopPropagation();
                  })
                }
              />
            );
          }

          if (source === TEASER_TYPES.IMAGE) {
            return (
              <ImageTeaser
                key={teaser.id}
                slide={teaser}
                currency={currency || defaultCurrency}
                className={styles.teaser}
                handleClickOnMove={(event) =>
                  handleClickOnMove(event, () =>
                    clickOnSlide({
                      slideName: teaser.url,
                      index
                    })
                  )
                }
              />
            );
          }

          const { eventsMarketsIds } = teaserLine;

          if (eventId && eventsMarketsIds[eventId]) {
            const [marketId] = eventsMarketsIds[eventId];
            const market = markets[marketId];
            const imageLink = getSlidePlatformImage(
              teaser,
              pageType,
              lang,
              currency || defaultCurrency,
              getImg
            );
            const { outcomeIds } = market || {};
            const outcomes = getOutcomesForMarket(outcomeIds, stateOutcomes);

            // todo refactor: SportTeaser does not accept 'outcomes'
            return (
              <SportTeaser
                key={teaser.id}
                className={styles.teaser}
                teaserPicture={imageLink}
                eventId={eventId}
                market={market}
                outcomes={outcomes}
                handleClickOnMove={(event) =>
                  handleClickOnMove(event, () =>
                    clickOnSlide({
                      slideName: teaser.teaserName,
                      index
                    })
                  )
                }
              />
            );
          }
          return null;
        })}
      </EventsSlider>
    </WidgetWrapper>
  );
};
